import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ProcessedMessage } from './useMessageProcessor';

export interface IframeManagerState {
  displayedIframeId: number | string | null;
  showPanel: boolean;
  isExpanded: boolean;
  isFullscreen: boolean;
  userSelected: boolean;
}

export interface IframeManagerActions {
  displayIframe: (messageId: number | string) => void;
  closePanel: () => void;
  toggleExpand: () => void;
  toggleFullscreen: () => void;
}

export interface IframeManagerResult {
  state: IframeManagerState;
  actions: IframeManagerActions;
  displayedMessage: ProcessedMessage | null;
}
export function useIframeManager(
  iframeMessages: ProcessedMessage[]
): IframeManagerResult {
  const [displayedIframeId, setDisplayedIframeId] = useState<number | string | null>(null);
  const [userSelected, setUserSelected] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const prevCountRef = useRef(0);
  
  // Auto-display logic (PRESERVE EXISTING BEHAVIOR)
  useEffect(() => {
    if (iframeMessages.length === 0) {
      prevCountRef.current = 0;
      return;
    }
    
    const hasNewMessage = iframeMessages.length > prevCountRef.current;
    const latestMessage = iframeMessages[iframeMessages.length - 1];
    
    // New iframe message arrived - auto-display it
    if (hasNewMessage) {
      setUserSelected(false);
      setDisplayedIframeId(latestMessage.id);
      setShowPanel(true);
      prevCountRef.current = iframeMessages.length;
      return;
    }
    
    prevCountRef.current = iframeMessages.length;
    
    // Current displayed iframe not found - show latest
    if (
      displayedIframeId === null ||
      !iframeMessages.some(m => m.id === displayedIframeId)
    ) {
      setDisplayedIframeId(latestMessage.id);
      setShowPanel(true);
      return;
    }
    
    // Auto-update to latest if user didn't manually select
    if (!userSelected && displayedIframeId !== latestMessage.id) {
      setDisplayedIframeId(latestMessage.id);
    }
  }, [iframeMessages, displayedIframeId, userSelected]);
  
  // Get currently displayed message
  const displayedMessage = useMemo(() => {
    if (!displayedIframeId) return null;
    
    const message = iframeMessages.find(m => m.id === displayedIframeId);
    if (!message) {
      return iframeMessages[iframeMessages.length - 1] || null;
    }
    
    return message;
  }, [displayedIframeId, iframeMessages]);
  
  // Actions
  const displayIframe = useCallback((messageId: number | string) => {
    setDisplayedIframeId(messageId);
    setUserSelected(true);
    setShowPanel(true);
  }, []);
  
  const closePanel = useCallback(() => {
    setShowPanel(false);
    setIsExpanded(false);
    setIsFullscreen(false);
  }, []);
  
  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);
  
  return {
    state: {
      displayedIframeId,
      showPanel,
      isExpanded,
      isFullscreen,
      userSelected,
    },
    actions: {
      displayIframe,
      closePanel,
      toggleExpand,
      toggleFullscreen,
    },
    displayedMessage,
  };
}

